import {
  getActiveDataTables,
  getConnectedDataTablesIdsFromElement,
} from '@shared/helpers/data-tables';
import { projectGuards } from '@shared/helpers/guards/project-guards.helpers';
import { IDataTable } from '@shared/models/data-table.model';
import { OneOfPropertyElements } from '@shared/models/project.model';
import { useCallback, useMemo } from 'react';
import { useDataTables } from './store';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';

/**
 * Apply data table properties to an element (handler merges/removes linked properties).
 */
export const useApplyDataTableProperties = (element: OneOfPropertyElements) => {
  const applyChanges = useApplyProjectChanges();
  return useCallback(
    (dataTable: IDataTable): void => {
      void applyChanges([
        {
          type: ProjectChangeType.ElementDataTablePropertiesApply,
          elementId: element.id,
          dataTable,
        },
      ]);
    },
    [applyChanges, element.id],
  );
};

/**
 * Active data tables that can be connected to {@link element}.
 * Building versions are omitted: {@link ProjectChangeType.ElementDataTablePropertiesApply}
 * only patches system elements, and data-table sync never runs on a version.
 * @param element
 */
export const useAvailableDataTables = (
  element: OneOfPropertyElements,
): IDataTable[] => {
  const dataTables = useDataTables();
  return useMemo(() => {
    if (projectGuards.is.buildingVersion(element)) return [];
    const usedIds = getConnectedDataTablesIdsFromElement(element);
    return getActiveDataTables(dataTables).filter(
      (dataTable) => !usedIds.includes(dataTable.id),
    );
  }, [dataTables, element]);
};
